import { useChartStore, useToothData } from '@/stores/chartStore'; // removed updateToothData
import { ToothCell } from './ToothCell';
import { getTeethByArch } from '@/lib/tooth-numbering';

// Wrapper component to properly use hooks
function ToothCellWrapper({ toothNumber, isUpper }: { toothNumber: number; isUpper: boolean }) {
  const toothData = useToothData(toothNumber);
  const { selectedTooth, selectTooth } = useChartStore();

  return (
    <ToothCell
      toothNumber={toothNumber}
      data={toothData}
      isSelected={selectedTooth === toothNumber}
      onClick={() => selectTooth(toothNumber)}
      onContextMenu={() => {
        // Add any context menu logic here if needed
      }}
      isUpper={isUpper}
    />
  );
}

export function ToothGrid() {
  const upperTeeth = getTeethByArch('upper');
  const lowerTeeth = getTeethByArch('lower');

  return (
    <div className="flex flex-col items-center w-full">
      {/* Upper Arch */}
      <h3 className="text-sm font-medium text-gray-500 mb-2 text-center">
        Upper Arch (Maxillary)
      </h3>
      
      {/* Upper Arch Table */}
      <table className="border-collapse">
        <tbody>
          {/* Upper Teeth Row */}
          <tr>
            {upperTeeth.map((toothNum) => (
              <td key={toothNum} className="p-0">
                <ToothCellWrapper
                  toothNumber={toothNum}
                  isUpper={true}
                />
              </td>
            ))}
          </tr>
          {/* Upper Buccal Sites */}
          <tr>
            {upperTeeth.map((toothNum) => (
              <td key={`upper-buccal-${toothNum}`} className="p-0">
                <SiteRow toothNumber={toothNum} surface="buccal" />
              </td>
            ))}
          </tr>
          {/* Upper Lingual Sites */}
          <tr>
            {upperTeeth.map((toothNum) => (
              <td key={`upper-lingual-${toothNum}`} className="p-0">
                <SiteRow toothNumber={toothNum} surface="lingual" />
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* Divider */}
      <div className="border-t border-gray-300 my-4 w-full" />

      {/* Lower Arch Table */}
      <table className="border-collapse">
        <tbody>
          {/* Lower Lingual Sites */}
          <tr>
            {lowerTeeth.map((toothNum) => (
              <td key={`lower-lingual-${toothNum}`} className="p-0">
                <SiteRow toothNumber={toothNum} surface="lingual" />
              </td>
            ))}
          </tr>
          {/* Lower Buccal Sites */}
          <tr>
            {lowerTeeth.map((toothNum) => (
              <td key={`lower-buccal-${toothNum}`} className="p-0">
                <SiteRow toothNumber={toothNum} surface="buccal" />
              </td>
            ))}
          </tr>
          {/* Lower Teeth Row */}
          <tr>
            {lowerTeeth.map((toothNum) => (
              <td key={toothNum} className="p-0">
                <ToothCellWrapper
                  toothNumber={toothNum}
                  isUpper={false}
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      
      <h3 className="text-sm font-medium text-gray-500 mt-2 text-center">
        Lower Arch (Mandibular)
      </h3>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-xs text-gray-500 pt-4 border-t w-full mt-6">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-clinical-healthy" />
          <span>Healthy (1-3mm)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-clinical-mild" />
          <span>Mild (4mm)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-clinical-moderate" />
          <span>Moderate (5mm)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-clinical-severe" />
          <span>Severe (6mm+)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-clinical-bleeding" />
          <span>Bleeding</span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface SiteRowProps {
  toothNumber: number;
  surface: 'buccal' | 'lingual';
}

function SiteRow({ toothNumber, surface }: SiteRowProps) {
  const toothData = useToothData(toothNumber);

  const sites = surface === 'buccal'
    ? ['MB', 'B', 'DB']
    : ['ML', 'L', 'DL'];

  const getSiteValue = (siteCode: string) => {
    if (!toothData?.sites) return null;
    const site = toothData.sites.find(s => s.site === siteCode);
    return site?.pocket_depth ?? null;
  };

  const getSiteColor = (depth: number | null) => {
    if (depth === null) return 'bg-gray-100 text-gray-400';
    if (depth <= 3) return 'bg-clinical-healthy text-white';
    if (depth === 4) return 'bg-clinical-mild text-white';
    if (depth === 5) return 'bg-clinical-moderate text-white';
    return 'bg-clinical-severe text-white';
  };

  const hasBleeding = (siteCode: string) => {
    if (!toothData?.sites) return false;
    const site = toothData.sites.find(s => s.site === siteCode);
    return site?.bleeding ?? false;
  };

  if (toothData?.status === 'missing') {
    return <div className="w-16 h-6" />;
  }

  return (
    <div className="flex w-16 gap-0.5 px-0.5">
      {sites.map((site) => {
        const value = getSiteValue(site);
        const bleeding = hasBleeding(site);

        return (
          <div
            key={site}
            className={`
              flex-1 h-6 flex items-center justify-center text-[10px] font-medium rounded-sm
              ${getSiteColor(value)}
              ${bleeding ? 'ring-1 ring-clinical-bleeding' : ''}
            `}
          >
            {value ?? '-'}
          </div>
        );
      })}
    </div>
  );
}